// src/pages/admin/CompanyProfile.jsx
// Owner fills company details once — used in invoices, POs, Tally export

import { useEffect, useState } from "react";
import { db } from "../../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { useCompany } from "../../context/CompanyContext";
// D-291 (P3b): GSTIN is checked (format + check character); the state is
// taken from the GSTIN's first 2 digits and must match it; new setting
// claims_itc ("We claim GST input credit") - only possible with a valid
// GSTIN. With ITC, purchase cost = rate before GST; without, cost incl. GST.
import { validateGstin } from "../../utils/gstBill";

// D-291 (P3b): added the Union Territories that were missing (a GSTIN
// from Chandigarh / J&K / Puducherry etc. had no state to match).
const INDIAN_STATES = [
  { name: "Andaman and Nicobar Islands", code: "35" },
  { name: "Andhra Pradesh",      code: "37" },
  { name: "Arunachal Pradesh",   code: "12" },
  { name: "Assam",               code: "18" },
  { name: "Bihar",               code: "10" },
  { name: "Chandigarh",          code: "04" },
  { name: "Chhattisgarh",        code: "22" },
  { name: "Dadra and Nagar Haveli and Daman and Diu", code: "26" },
  { name: "Delhi",               code: "07" },
  { name: "Goa",                 code: "30" },
  { name: "Gujarat",             code: "24" },
  { name: "Haryana",             code: "06" },
  { name: "Himachal Pradesh",    code: "02" },
  { name: "Jammu and Kashmir",   code: "01" },
  { name: "Jharkhand",           code: "20" },
  { name: "Karnataka",           code: "29" },
  { name: "Kerala",              code: "32" },
  { name: "Ladakh",              code: "38" },
  { name: "Lakshadweep",         code: "31" },
  { name: "Madhya Pradesh",      code: "23" },
  { name: "Maharashtra",         code: "27" },
  { name: "Manipur",             code: "14" },
  { name: "Meghalaya",           code: "17" },
  { name: "Mizoram",             code: "15" },
  { name: "Nagaland",            code: "13" },
  { name: "Odisha",              code: "21" },
  { name: "Puducherry",          code: "34" },
  { name: "Punjab",              code: "03" },
  { name: "Rajasthan",           code: "08" },
  { name: "Sikkim",              code: "11" },
  { name: "Tamil Nadu",          code: "33" },
  { name: "Telangana",           code: "36" },
  { name: "Tripura",             code: "16" },
  { name: "Uttar Pradesh",       code: "09" },
  { name: "Uttarakhand",         code: "05" },
  { name: "West Bengal",         code: "19" },
  { name: "Other Territory",     code: "97" },
];

const DEFAULT_TALLY = {
  tally_sales_ledger:    "Service Revenue",
  tally_purchase_ledger: "Purchases",
  tally_expense_ledger:  "Indirect Expenses",
  tally_cash_ledger:     "Cash",
  tally_bank_ledger:     "Bank Account",
  tally_cgst_ledger:     "Output CGST",
  tally_sgst_ledger:     "Output SGST",
  tally_igst_ledger:     "Output IGST",
  tally_input_cgst:      "Input CGST",
  tally_input_sgst:      "Input SGST",
  tally_input_igst:      "Input IGST",
  // D-291 P3d-5c: purchase bills + payments to Tally
  tally_round_off_ledger:        "Round Off",
  tally_purchase_charges_ledger: "",            // blank = the Purchase Ledger
  tally_tds_ledger:              "TDS Payable",
  tally_transport_ledger:        "",            // blank = the Expense Ledger
  tally_subcontract_ledger:      "Subcontract Charges",
};

export default function CompanyProfile() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";
  const { refreshCompany } = useCompany();

  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(true);
  const [saved,   setSaved]   = useState(false);
  const [tab,     setTab]     = useState("basic"); // "basic" | "tally"
  const [stateFixedFrom, setStateFixedFrom] = useState(null); // LOW-12: saved state did not match the GSTIN

  const [form, setForm] = useState({
    // Basic
    company_name:    "",
    company_address: "",
    company_phone:   "",
    company_email:   "",
    // GST
    gst_number:      "",
    pan_number:      "",
    state:           "",
    state_code:      "",
    claims_itc:      true, // "We claim GST input credit" - only with a valid GSTIN
    // Bank details — printed on invoices, below Terms & Conditions
    bank_name:       "",
    bank_account_no: "",
    bank_ifsc:       "",
    bank_branch:     "",
    terms_conditions: "1. Goods once sold will not be taken back or exchanged.\n2. All disputes are subject to local jurisdiction only.\n3. Interest will be charged on unpaid invoices after the due date.",
    // Tally
    ...DEFAULT_TALLY,
  });

  useEffect(() => { if (companyId) loadProfile(); }, [companyId]);

  const loadProfile = async () => {
    try {
      const snap = await getDoc(doc(db, "companies", companyId));
      if (snap.exists()) {
        const data = snap.data();
        // LOW-12: a profile saved before the GSTIN check (D-291 P3b) can hold a state that
        // does not match its own GSTIN. The form takes the GSTIN's state and says so; Save stores it.
        const savedGst = validateGstin(data.gst_number || "");
        const gstState = savedGst.ok ? INDIAN_STATES.find(s => s.code === savedGst.state_code) : null;
        if (gstState && (data.state_code || "") !== gstState.code) {
          setStateFixedFrom({ name: data.state || "no state", code: data.state_code || "-", to: `${gstState.name} (${gstState.code})` });
        }
        setForm(prev => ({
          ...prev,
          company_name:    data.company_name    || "",
          company_address: data.company_address || "",
          company_phone:   data.company_phone   || "",
          company_email:   data.company_email   || "",
          gst_number:      data.gst_number      || "",
          pan_number:      data.pan_number      || "",
          state:           gstState ? gstState.name : (data.state || ""),
          state_code:      gstState ? gstState.code : (data.state_code || ""),
          claims_itc:      data.claims_itc !== false, // missing = Yes
          bank_name:       data.bank_name       || "",
          bank_account_no: data.bank_account_no || "",
          bank_ifsc:       data.bank_ifsc       || "",
          bank_branch:     data.bank_branch     || "",
          terms_conditions: data.terms_conditions || prev.terms_conditions,
          // Tally — fallback to defaults
          tally_sales_ledger:    data.tally_sales_ledger    || DEFAULT_TALLY.tally_sales_ledger,
          tally_purchase_ledger: data.tally_purchase_ledger || DEFAULT_TALLY.tally_purchase_ledger,
          tally_expense_ledger:  data.tally_expense_ledger  || DEFAULT_TALLY.tally_expense_ledger,
          tally_cash_ledger:     data.tally_cash_ledger     || DEFAULT_TALLY.tally_cash_ledger,
          tally_bank_ledger:     data.tally_bank_ledger     || DEFAULT_TALLY.tally_bank_ledger,
          tally_cgst_ledger:     data.tally_cgst_ledger     || DEFAULT_TALLY.tally_cgst_ledger,
          tally_sgst_ledger:     data.tally_sgst_ledger     || DEFAULT_TALLY.tally_sgst_ledger,
          tally_igst_ledger:     data.tally_igst_ledger     || DEFAULT_TALLY.tally_igst_ledger,
          tally_input_cgst:      data.tally_input_cgst      || DEFAULT_TALLY.tally_input_cgst,
          tally_input_sgst:      data.tally_input_sgst      || DEFAULT_TALLY.tally_input_sgst,
          tally_input_igst:      data.tally_input_igst      || DEFAULT_TALLY.tally_input_igst,
          tally_round_off_ledger:        data.tally_round_off_ledger        || DEFAULT_TALLY.tally_round_off_ledger,
          tally_purchase_charges_ledger: data.tally_purchase_charges_ledger || "",
          tally_tds_ledger:              data.tally_tds_ledger              || DEFAULT_TALLY.tally_tds_ledger,
          tally_transport_ledger:        data.tally_transport_ledger        || "",
          tally_subcontract_ledger:      data.tally_subcontract_ledger      || DEFAULT_TALLY.tally_subcontract_ledger,
        }));
      }
    } catch (err) {
      console.error("Load profile error:", err);
    } finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // GSTIN typed -> upper case; once it is valid, the state comes from its
  // first 2 digits (the state and the GSTIN can never disagree).
  const handleGstinChange = (e) => {
    const value = e.target.value.toUpperCase();
    const check = validateGstin(value);
    const state = check.ok ? INDIAN_STATES.find(s => s.code === check.state_code) : null;
    setForm(prev => ({
      ...prev,
      gst_number: value,
      ...(state ? { state: state.name, state_code: state.code } : {}),
    }));
  };

  const gstCheck = form.gst_number.trim() ? validateGstin(form.gst_number) : null;

  const handleStateChange = (stateName) => {
    const state = INDIAN_STATES.find(s => s.name === stateName);
    setForm(prev => ({
      ...prev,
      state:      stateName,
      state_code: state?.code || "",
    }));
  };

  const handleSave = async () => {
    if (!form.company_name.trim()) { alert("Company name is required"); return; }
    let gstNumber = "";
    if (form.gst_number.trim()) {
      const check = validateGstin(form.gst_number);
      if (!check.ok) { alert(check.reason); return; }
      if (form.state_code && form.state_code !== check.state_code) {
        alert(`The state (code ${form.state_code}) does not match the GSTIN (state code ${check.state_code}).`);
        return;
      }
      gstNumber = check.gstin;
    }
    try {
      setSaving(true);
      await setDoc(doc(db, "companies", companyId), {
        ...form,
        gst_number:  gstNumber,
        // No GSTIN = cannot claim GST input credit (D-291)
        claims_itc:  gstNumber ? form.claims_itc !== false : false,
        company_id:  companyId,
        updated_at:  serverTimestamp(),
        updated_by:  user.uid || user.id || "",
      }, { merge: true });

      setSaved(true);
      setStateFixedFrom(null);
      refreshCompany(); // update global context
      setTimeout(() => setSaved(false), 4000);
    } catch (err) {
      console.error("Save profile error:", err);
      alert("Failed to save. Try again.");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6"><p className="text-gray-500">Loading...</p></div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Company Profile</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            This information appears on all invoices, POs and Tally exports.
          </p>
        </div>
        {saved && (
          <span className="text-green-600 font-semibold text-sm bg-green-50 px-4 py-2 rounded-xl">
            ✅ Saved
          </span>
        )}
      </div>

      {/* TABS */}
      <div className="flex gap-2">
        {[
          { key: "basic",  label: "🏢 Company Details" },
          { key: "tally",  label: "📊 Tally Ledger Mapping" },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
              tab === t.key
                ? "bg-slate-800 text-white"
                : "bg-white border border-slate-200 text-slate-600"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* BASIC TAB */}
      {tab === "basic" && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-5">

          <Section title="Company Information">
            <Field label="Company Name *" name="company_name" value={form.company_name}
              onChange={handleChange} placeholder="e.g. DVAC Solutions Pvt Ltd" />
            <Field label="Address" name="company_address" value={form.company_address}
              onChange={handleChange} placeholder="Full address" textarea />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Phone" name="company_phone" value={form.company_phone}
                onChange={handleChange} placeholder="9999XXXXXX" />
              <Field label="Email" name="company_email" value={form.company_email}
                onChange={handleChange} placeholder="billing@company.com" />
            </div>
          </Section>

          <Section title="GST & Tax Details">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Field label="GSTIN" name="gst_number" value={form.gst_number}
                  onChange={handleGstinChange} placeholder="15 characters, e.g. 09AAQFB2219L1Z5" />
                {gstCheck && (
                  <p className={`text-xs mt-1 ${gstCheck.ok ? "text-green-600" : "text-red-500"}`}>
                    {gstCheck.ok ? `Valid GSTIN · state code ${gstCheck.state_code}` : gstCheck.reason}
                  </p>
                )}
              </div>
              <Field label="PAN Number" name="pan_number" value={form.pan_number}
                onChange={handleChange} placeholder="XXXXX1234X" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">
                State <span className="text-slate-400 font-normal normal-case">(for CGST/SGST vs IGST)</span>
              </label>
              <select
                value={form.state}
                disabled={!!gstCheck?.ok}
                title={gstCheck?.ok ? "Taken from your GSTIN" : undefined}
                onChange={e => handleStateChange(e.target.value)}
                className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 outline-none">
                <option value="">Select State</option>
                {INDIAN_STATES.map(s => (
                  <option key={s.code} value={s.name}>{s.name} ({s.code})</option>
                ))}
              </select>
              {stateFixedFrom && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-1">
                  The saved state ({stateFixedFrom.name} · {stateFixedFrom.code}) did not match your GSTIN - it is now {stateFixedFrom.to}. Press Save Company Profile to store it (invoices use this state for CGST/SGST vs IGST).
                </p>
              )}
              {form.state_code && (
                <p className="text-xs text-slate-400 mt-1">
                  State Code: <strong>{form.state_code}</strong> — used for GST calculations{gstCheck?.ok ? " · taken from your GSTIN" : ""}
                </p>
              )}
            </div>

            {/* D-291: GST input credit (ITC) — decides the purchase cost Zeno uses */}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">
                GST input credit (ITC) on purchases
              </label>
              {gstCheck?.ok ? (
                <div className="space-y-2">
                  {[
                    { value: true,  label: "Yes — we claim GST input credit",
                      help: "Stock and project cost use the rate BEFORE GST. GST on bills is kept separately." },
                    { value: false, label: "No — we do not claim it",
                      help: "Stock and project cost use the rate INCLUDING GST." },
                  ].map(opt => (
                    <label key={String(opt.value)}
                      className={`flex items-start gap-2 p-3 rounded-xl border text-sm cursor-pointer ${
                        form.claims_itc === opt.value ? "border-slate-800 bg-slate-50" : "border-slate-200"}`}>
                      <input type="radio" name="claims_itc" className="mt-1"
                        checked={form.claims_itc === opt.value}
                        onChange={() => setForm(prev => ({ ...prev, claims_itc: opt.value }))} />
                      <span>
                        <span className="font-semibold text-slate-700">{opt.label}</span>
                        <span className="block text-xs text-slate-500">{opt.help}</span>
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-3">
                  No valid GSTIN — GST input credit cannot be claimed. Purchase cost will include GST.
                </p>
              )}
            </div>
          </Section>

          <Section title="Bank Details">
            <p className="text-xs text-slate-400 -mt-1 mb-2">Printed on every invoice for client payments.</p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Bank Name" name="bank_name" value={form.bank_name}
                onChange={handleChange} placeholder="e.g. Your Bank Name" />
              <Field label="Branch" name="bank_branch" value={form.bank_branch}
                onChange={handleChange} placeholder="e.g. Your Branch Name" />
              <Field label="Account Number" name="bank_account_no" value={form.bank_account_no}
                onChange={handleChange} placeholder="Your account number" />
              <Field label="IFSC Code" name="bank_ifsc" value={form.bank_ifsc}
                onChange={handleChange} placeholder="Your bank's IFSC code" />
            </div>
          </Section>

          <Section title="Terms and Conditions">
            <p className="text-xs text-slate-400 -mt-1 mb-2">Printed on every invoice, next to Bank Details.</p>
            <Field label="Terms" name="terms_conditions" value={form.terms_conditions}
              onChange={handleChange} placeholder="One point per line" textarea rows={5} />
          </Section>
        </div>
      )}

      {/* TALLY TAB */}
      {tab === "tally" && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-5">

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700">
            <strong>Important:</strong> Enter the exact ledger names from your Tally software.
            These must match exactly — case sensitive. Default values are pre-filled with common names.
          </div>

          <Section title="Sales & Purchase Ledgers">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Sales Ledger" name="tally_sales_ledger" value={form.tally_sales_ledger}
                onChange={handleChange} placeholder="Service Revenue" />
              <Field label="Purchase Ledger" name="tally_purchase_ledger" value={form.tally_purchase_ledger}
                onChange={handleChange} placeholder="Purchases" />
              <Field label="Expense Ledger" name="tally_expense_ledger" value={form.tally_expense_ledger}
                onChange={handleChange} placeholder="Indirect Expenses" />
            </div>
          </Section>

          <Section title="Cash & Bank Ledgers">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Cash Ledger" name="tally_cash_ledger" value={form.tally_cash_ledger}
                onChange={handleChange} placeholder="Cash" />
              <Field label="Bank Ledger" name="tally_bank_ledger" value={form.tally_bank_ledger}
                onChange={handleChange} placeholder="HDFC Bank" />
            </div>
          </Section>

          <Section title="GST Ledgers (Output — Sales)">
            <div className="grid grid-cols-3 gap-4">
              <Field label="Output CGST" name="tally_cgst_ledger" value={form.tally_cgst_ledger}
                onChange={handleChange} placeholder="Output CGST" />
              <Field label="Output SGST" name="tally_sgst_ledger" value={form.tally_sgst_ledger}
                onChange={handleChange} placeholder="Output SGST" />
              <Field label="Output IGST" name="tally_igst_ledger" value={form.tally_igst_ledger}
                onChange={handleChange} placeholder="Output IGST" />
            </div>
          </Section>

          <Section title="GST Ledgers (Input — Purchases)">
            <div className="grid grid-cols-3 gap-4">
              <Field label="Input CGST" name="tally_input_cgst" value={form.tally_input_cgst}
                onChange={handleChange} placeholder="Input CGST" />
              <Field label="Input SGST" name="tally_input_sgst" value={form.tally_input_sgst}
                onChange={handleChange} placeholder="Input SGST" />
              <Field label="Input IGST" name="tally_input_igst" value={form.tally_input_igst}
                onChange={handleChange} placeholder="Input IGST" />
              {/* D-291 P3d-5c: purchase bills + payments to Tally */}
              <Field label="Round-off Ledger" name="tally_round_off_ledger" value={form.tally_round_off_ledger}
                onChange={handleChange} placeholder="Round Off" />
              <Field label="Purchase Charges Ledger (blank = Purchase Ledger)" name="tally_purchase_charges_ledger" value={form.tally_purchase_charges_ledger}
                onChange={handleChange} placeholder="Freight & Packing Inward" />
              <Field label="TDS Payable Ledger" name="tally_tds_ledger" value={form.tally_tds_ledger}
                onChange={handleChange} placeholder="TDS Payable" />
              <Field label="Transport Ledger (blank = Expense Ledger)" name="tally_transport_ledger" value={form.tally_transport_ledger}
                onChange={handleChange} placeholder="Freight Outward" />
              <Field label="Subcontract Charges Ledger" name="tally_subcontract_ledger" value={form.tally_subcontract_ledger}
                onChange={handleChange} placeholder="Subcontract Charges" />
            </div>
          </Section>
        </div>
      )}

      {/* SAVE */}
      <button onClick={handleSave} disabled={saving}
        className={`w-full py-3.5 text-white rounded-xl font-semibold text-sm disabled:opacity-50 transition ${saved ? "bg-green-600 hover:bg-green-700" : "bg-slate-800 hover:bg-slate-900"}`}>
        {saving ? "Saving..." : saved ? "✅ Saved Successfully!" : "Save Company Profile"}
      </button>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────
function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-bold text-slate-700 mb-3 pb-2 border-b border-slate-100">
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, name, value, onChange, placeholder, textarea, rows = 2 }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">{label}</label>
      {textarea ? (
        <textarea name={name} value={value} onChange={onChange}
          placeholder={placeholder} rows={rows}
          className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-slate-300 resize-none" />
      ) : (
        <input name={name} value={value} onChange={onChange}
          placeholder={placeholder}
          className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-slate-300" />
      )}
    </div>
  );
}
